import { useClerk, useUser } from "@clerk/clerk-react";
import { LogOut, MessageCircle, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ChatSidebar } from "@/components/ChatSidebar";
import { NotificationBell } from "@/components/NotificationBell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChatSidebarProvider, useChatSidebar } from "@/contexts/ChatSidebarContext";
import { isAdminRole } from "@/lib/roles";

interface MinimalLayoutProps {
  children: React.ReactNode;
}

function MinimalTopBar() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const chatSidebar = useChatSidebar();

  const isAdmin = isAdminRole(user?.publicMetadata?.role as string | undefined);

  return (
    <div className="h-14 bg-sidebar border-b border-sidebar-border px-4 md:px-6 flex items-center justify-between">
      {/* Logo */}
      <div className="flex items-center gap-3">
        <img src="/dfr-logo.svg" alt="DFR Logo" className="h-8 w-auto" />
        <h1 className="text-base font-semibold text-sidebar-foreground hidden sm:block">
          Dominion Programme
        </h1>
      </div>

      {/* Right Side - Icons and User */}
      <div className="flex items-center gap-3 md:gap-4">
        <NotificationBell />

        {/* Chat Icon */}
        {chatSidebar && (
          <button
            onClick={chatSidebar.toggle}
            className="w-9 h-9 border border-sidebar-border flex items-center justify-center hover:bg-sidebar-accent transition-colors"
            aria-label="Open chat"
          >
            <MessageCircle className="w-4 h-4 text-sidebar-foreground" />
          </button>
        )}

        {/* User Info */}
        <div className="flex items-center gap-2 md:gap-3 ml-1 md:ml-2">
          <span className="hidden sm:block text-sidebar-foreground text-sm font-medium">
            {user?.firstName || user?.emailAddresses[0]?.emailAddress.split("@")[0] || "User"}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="focus:outline-none focus:ring-2 focus:ring-sidebar-border">
                <Avatar className="w-8 h-8 cursor-pointer">
                  <AvatarImage src={user?.imageUrl} />
                  <AvatarFallback className="bg-sidebar-accent text-sidebar-foreground text-sm border border-sidebar-border">
                    {user?.firstName?.[0] ||
                      user?.emailAddresses[0]?.emailAddress[0]?.toUpperCase() ||
                      "U"}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isAdmin && (
                <>
                  <DropdownMenuItem onClick={() => navigate("/admin")}>
                    <Shield className="w-4 h-4" />
                    Admin Interface
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => signOut()}>
                <LogOut className="w-4 h-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}

export function MinimalLayout({ children }: MinimalLayoutProps) {
  return (
    <ChatSidebarProvider>
      <div className="flex min-h-screen bg-content">
        <div className="flex-1 flex flex-col min-w-0">
          <MinimalTopBar />
          <main className="flex-1 overflow-auto p-4 md:p-6 text-content-foreground">
            {children}
          </main>
        </div>
        <ChatSidebar />
      </div>
    </ChatSidebarProvider>
  );
}
